import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Fragment, ReactNode, useState } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { Button, type ButtonSize, type ButtonVariant } from "../Button/Button";
import { ButtonDropdown } from "../ButtonDropdown/ButtonDropdown";
import { IconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";
import menuStyles from "../ButtonDropdown/ButtonDropdown.module.scss";
import styles from "./HeaderActions.module.scss";

export type HeaderActionMenuItem = {
    id: string;
    label: ReactNode;
    icon?: ReactNode;
    onSelect?: () => void;
    disabled?: boolean;
    /** Destructive styling (red) in the overflow menu. */
    destructive?: boolean;
};

export type HeaderActionItem = {
    /** Stable id (React key). */
    id: string;
    /** Button / menu label. */
    label: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    /** Shows spinner on the desktop button; disables the menu row. */
    loading?: boolean;
    /** Leading icon (desktop button + mobile menu row). */
    icon?: ReactNode;
    /** Desktop button variant. Default `primary`. */
    variant?: ButtonVariant;
    /** Desktop button size. Default `md`. */
    size?: ButtonSize;
    /** Optional tooltip shown on the desktop button. */
    tooltip?: ReactNode;
    /** Extra class on the desktop `Button` / `ButtonDropdown`. */
    className?: string;
    /** Destructive styling (red) in the overflow menu. */
    destructive?: boolean;
    /**
     * Optional submenu. Desktop renders a `ButtonDropdown`; mobile flattens
     * these entries into the overflow menu.
     */
    menuItems?: HeaderActionMenuItem[];
};

export type HeaderActionsProps = {
    actions: HeaderActionItem[];
    /**
     * Accessible name for the mobile overflow trigger.
     * Default: `"Actions"`.
     */
    "aria-label"?: string;
    className?: string;
    /** Dropdown alignment on mobile. Default `end`. */
    align?: "start" | "center" | "end";
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
};

const MoreIcon = () => <Icon icon="more" />;

/**
 * Treez DS — HeaderActions
 *
 * Responsive action cluster for panel / form headers:
 * - Desktop (≥1024): full `Button`s / `ButtonDropdown`s in a row
 * - Below 1024 **and** more than one action: `IconButton` overflow menu
 * - A single action always renders as a full button (all breakpoints)
 * - Actions with `menuItems` flatten into the mobile overflow menu
 */
export function HeaderActions({
    actions,
    className,
    align = "end",
    side = "bottom",
    sideOffset = 6,
    "aria-label": ariaLabel = "Actions",
}: HeaderActionsProps) {
    const [open, setOpen] = useState(false);
    const collapseToMenu = actions.length > 1;
    const allDisabled =
        actions.length === 0 ||
        actions.every(action => {
            if (action.menuItems?.length) {
                return (
                    action.disabled ||
                    action.menuItems.every(item => item.disabled)
                );
            }
            return action.disabled || action.loading;
        });

    const buttons = actions.map(action => {
        if (action.menuItems?.length) {
            return (
                <ButtonDropdown
                    className={action.className}
                    disabled={action.disabled}
                    iconLeft={action.icon}
                    items={action.menuItems.map(item => ({
                        id: item.id,
                        label: item.label,
                        icon: item.icon,
                        disabled: item.disabled || action.disabled,
                        onSelect: () => item.onSelect?.(),
                    }))}
                    key={action.id}
                    size={action.size ?? "md"}
                    variant={action.variant ?? "primary"}
                >
                    {action.label}
                </ButtonDropdown>
            );
        }

        const button = (
            <Button
                className={action.className}
                disabled={action.disabled || action.loading}
                iconLeft={action.loading ? undefined : action.icon}
                key={action.id}
                loading={action.loading}
                onClick={action.onClick}
                size={action.size ?? "md"}
                type="button"
                variant={action.variant ?? "primary"}
            >
                {action.label}
            </Button>
        );

        if (action.tooltip) {
            return (
                <Tooltip content={action.tooltip} key={action.id} side="bottom">
                    {button}
                </Tooltip>
            );
        }

        return button;
    });

    return (
        <div className={cx(styles.root, className)} data-ui="header-actions">
            <div
                className={collapseToMenu ? styles.desktop : styles.always}
                role="group"
            >
                {buttons}
            </div>

            {collapseToMenu ? (
                <div className={styles.mobile}>
                    <DropdownMenu.Root onOpenChange={setOpen} open={open}>
                        <DropdownMenu.Trigger asChild>
                            <IconButton
                                aria-label={ariaLabel}
                                disabled={allDisabled}
                                size="sm"
                                type="button"
                                variant="primary"
                            >
                                <MoreIcon />
                            </IconButton>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Portal>
                            <DropdownMenu.Content
                                align={align}
                                className={cx(menuStyles.content, styles.menu)}
                                data-ui="header-actions-menu"
                                loop
                                side={side}
                                sideOffset={sideOffset}
                            >
                                {actions.map(action => {
                                    if (action.menuItems?.length) {
                                        return (
                                            <Fragment key={action.id}>
                                                {action.menuItems.map(item => (
                                                    <DropdownMenu.Item
                                                        className={cx(
                                                            menuStyles.item,
                                                            item.destructive &&
                                                                menuStyles.itemDestructive,
                                                        )}
                                                        disabled={
                                                            action.disabled ||
                                                            item.disabled
                                                        }
                                                        key={`${action.id}-${item.id}`}
                                                        onSelect={() =>
                                                            item.onSelect?.()
                                                        }
                                                    >
                                                        {item.icon ||
                                                        action.icon ? (
                                                            <span
                                                                aria-hidden
                                                                className={
                                                                    styles.menuIcon
                                                                }
                                                            >
                                                                {item.icon ??
                                                                    action.icon}
                                                            </span>
                                                        ) : null}
                                                        <span
                                                            className={
                                                                styles.menuLabel
                                                            }
                                                        >
                                                            {item.label}
                                                        </span>
                                                    </DropdownMenu.Item>
                                                ))}
                                            </Fragment>
                                        );
                                    }

                                    return (
                                        <DropdownMenu.Item
                                            className={cx(
                                                menuStyles.item,
                                                action.destructive &&
                                                    menuStyles.itemDestructive,
                                            )}
                                            disabled={
                                                action.disabled ||
                                                action.loading
                                            }
                                            key={action.id}
                                            onSelect={() => action.onClick?.()}
                                        >
                                            {action.icon ? (
                                                <span
                                                    aria-hidden
                                                    className={styles.menuIcon}
                                                >
                                                    {action.icon}
                                                </span>
                                            ) : null}
                                            <span className={styles.menuLabel}>
                                                {action.label}
                                            </span>
                                        </DropdownMenu.Item>
                                    );
                                })}
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </div>
            ) : null}
        </div>
    );
}

HeaderActions.displayName = "HeaderActions";
